import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Edit3, Check } from 'lucide-react';
import { ToolType, generateId } from './types';

const canAnnotateWithTool = (tool) => (
  tool === ToolType.PEN || tool === ToolType.MARKER || tool === ToolType.ERASER
);

const getPointerPos = (event, canvas) => {
  const rect = canvas.getBoundingClientRect();
  return {
    x: (event.clientX - rect.left) / Math.max(1, rect.width),
    y: (event.clientY - rect.top) / Math.max(1, rect.height),
  };
};

const drawPath = (ctx, path, width, height) => {
  const points = path.points || [];
  if (points.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(points[0].x * width, points[0].y * height);
  for (let i = 1; i < points.length; i += 1) {
    ctx.lineTo(points[i].x * width, points[i].y * height);
  }
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = path.width || 3;
  if (path.tool === ToolType.ERASER) {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.globalAlpha = 1;
    ctx.strokeStyle = '#000';
  } else if (path.tool === ToolType.MARKER) {
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 0.45;
    ctx.strokeStyle = path.color || '#000';
  } else {
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.strokeStyle = path.color || '#000';
  }
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
};

const WorksheetElement = ({
  element,
  currentTool,
  currentColor,
  isAnnotating,
  onToggleAnnotating,
  onUpdate,
}) => {
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const draftPathRef = useRef(null);
  const drawRafRef = useRef(null);
  const annotationsRef = useRef(element.annotations);
  const [isDrawing, setIsDrawing] = useState(false);

  // Keep annotationsRef current without triggering re-renders
  annotationsRef.current = element.annotations;

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage) return;

    const rect = stage.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.floor(rect.width));
    const height = Math.max(1, Math.floor(rect.height));
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const base = Array.isArray(annotationsRef.current) ? annotationsRef.current : [];
    [...base, draftPathRef.current].filter(Boolean).forEach((path) => {
      drawPath(ctx, path, width, height);
    });
  }, []); // stable — reads from refs

  const scheduleRedraw = useCallback(() => {
    if (drawRafRef.current != null) return;
    drawRafRef.current = window.requestAnimationFrame(() => {
      drawRafRef.current = null;
      redraw();
    });
  }, [redraw]);

  // Redraw when annotations change or dimensions change
  useEffect(() => {
    redraw();
  }, [redraw, element.annotations, element.width, element.height]);

  useEffect(() => {
    const handleResize = () => scheduleRedraw();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [scheduleRedraw]);

  useEffect(() => () => {
    if (drawRafRef.current != null) {
      window.cancelAnimationFrame(drawRafRef.current);
      drawRafRef.current = null;
    }
  }, []);

  const commitDraftPath = useCallback(() => {
    const draft = draftPathRef.current;
    draftPathRef.current = null;
    setIsDrawing(false);
    if (!draft || (draft.points || []).length < 2) {
      scheduleRedraw();
      return;
    }
    const annotations = Array.isArray(annotationsRef.current) ? annotationsRef.current : [];
    onUpdate({ annotations: [...annotations, draft] });
  }, [onUpdate, scheduleRedraw]);

  const handlePointerDown = useCallback((event) => {
    if (!isAnnotating || !canAnnotateWithTool(currentTool)) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    event.preventDefault();
    event.stopPropagation();
    canvas.setPointerCapture?.(event.pointerId);
    const point = getPointerPos(event, canvas);
    draftPathRef.current = {
      id: generateId(),
      tool: currentTool,
      color: currentColor,
      width: currentTool === ToolType.MARKER ? 14 : (currentTool === ToolType.ERASER ? 20 : 3),
      points: [point],
    };
    setIsDrawing(true);
  }, [currentColor, currentTool, isAnnotating]);

  const handlePointerMove = useCallback((event) => {
    if (!isAnnotating || !draftPathRef.current || !isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    event.preventDefault();
    event.stopPropagation();
    const events = typeof event.getCoalescedEvents === 'function' ? event.getCoalescedEvents() : [event];
    const nextPoints = events.map((e) => getPointerPos(e, canvas));
    draftPathRef.current = {
      ...draftPathRef.current,
      points: [...(draftPathRef.current.points || []), ...nextPoints],
    };
    scheduleRedraw();
  }, [isAnnotating, isDrawing, scheduleRedraw]);

  const handlePointerUp = useCallback((event) => {
    if (!isAnnotating || !isDrawing) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget?.releasePointerCapture?.(event.pointerId);
    commitDraftPath();
  }, [commitDraftPath, isAnnotating, isDrawing]);

  return (
    <div className="wb-worksheet">
      <div className="wb-worksheet__topbar">
        <button
          className={`wb-worksheet__annotate-btn ${isAnnotating ? 'active' : ''}`}
          onClick={(event) => {
            event.stopPropagation();
            onToggleAnnotating();
          }}
          title={isAnnotating ? 'Done annotating' : 'Annotate worksheet'}
        >
          {isAnnotating ? <Check size={13} /> : <Edit3 size={13} />}
          <span>{isAnnotating ? 'Done' : 'Annotate'}</span>
        </button>
      </div>
      <div className="wb-worksheet__stage" ref={stageRef}>
        <img src={element.src} alt={element.pdfMeta?.filename || 'Worksheet'} className="wb-worksheet__image" draggable={false} />
        <canvas
          ref={canvasRef}
          className={`wb-worksheet__canvas ${isAnnotating ? 'annotating' : ''}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onWheel={(event) => event.stopPropagation()}
          style={{ touchAction: 'none' }}
        />
      </div>
    </div>
  );
};

export default WorksheetElement;
